/**
 * Why an address is on the do-not-contact list, and which of those reasons an
 * operator may take back. Named once for both ends.
 *
 * THE SECOND LIST IS THE LOAD-BEARING ONE. Some entries record the OPERATOR's
 * decision and can be undone by the operator: a row added by hand, a row that
 * came in with an import, an address that bounced. Others record what the
 * PERSON decided about being written to, and an operator undoing one of those
 * is an opt-out that stops working - which is the single failure this whole
 * module is built to avoid.
 *
 * Both lists live here rather than beside either caller for the same reason
 * `RUN_STOPS` does. The API validates a filter against the first, the delete
 * route refuses on the second, and the interface renders a label per source; a
 * source added in one of those three places and forgotten in the others is not
 * a crash. It is a filter that rejects a real value, or a row that quietly
 * becomes deletable, or a screen printing `marketing.suppression.source.erasure`
 * where a word belongs.
 *
 * Pure module. No clock, no DOM, no imports.
 */

export const MKT_SUPPRESSION_SOURCES = [
  /** They clicked the link, or their mail client posted the one-click header. */
  "unsubscribe",
  /** A hard bounce. The address does not exist. */
  "bounce",
  /** They reported us as spam. */
  "complaint",
  /** An operator typed it in - usually after reading a reply that said stop. */
  "manual",
  /** Carried in from somewhere else. */
  "import",
  /**
   * They asked, under article 17, to have their data deleted.
   *
   * Its own source rather than `manual`, and the difference is not cosmetic:
   * this row is what remains AFTER everything else about them was removed, so
   * deleting it would not merely un-suppress them, it would leave nothing at
   * all saying they had ever asked. The next audience built off the register
   * would enrol them again, with no trace of the request that was supposed to
   * be honoured for ever.
   */
  "erasure",
] as const;

export type MktSuppressionSource = (typeof MKT_SUPPRESSION_SOURCES)[number];

/**
 * Sources an operator may NOT delete from the list.
 *
 * Each one records something the person decided about themselves. `bounce` is
 * deliberately absent: a dead mailbox is a fact about an address rather than a
 * decision by a human, and an address that comes back to life should be
 * reachable again.
 */
export const MKT_NON_REMOVABLE_SOURCES = ["unsubscribe", "complaint", "erasure"] as const;

export type MktNonRemovableSource = (typeof MKT_NON_REMOVABLE_SOURCES)[number];

export function isRemovableSuppression(source: string): boolean {
  return !(MKT_NON_REMOVABLE_SOURCES as readonly string[]).includes(source);
}
